import React, { useState } from 'react';
import { Alert, Button, Chip, Paper, Stack, Typography } from '@mui/material';
import { AccountTree, Close } from '@mui/icons-material';

import {
  IKiKontrollstrukturAblageProposal,
  IKiKontrollstrukturProposal,
} from '../Interfaces/IKi';
import { KiKontrollstruktur, startNurWurzel } from './KiKontrollstruktur';
import { KontrollGraph, kontrollStore } from './kontrollstruktur';

const vorgangLabel = (vorgang: string | null, aktenzeichen: string | null) =>
  `Vorgang: ${vorgang ?? '?'}${aktenzeichen ? ` (${aktenzeichen})` : ''}`;

/**
 * Vorschlagskarte: Eigentümer- und Kontrollstruktur prüfen (#1336).
 *
 * Abgerufen wird erst nach dem Klick — jede Gesellschaft kostet einen
 * Registerabruf. Nennt die Karte einen Vorgang, fragt sie danach, ob die
 * Struktur in dessen GwG-Ordner soll.
 */
export const KiKontrollstrukturProposalCard: React.FC<{
  proposal: IKiKontrollstrukturProposal;
}> = ({ proposal }) => {
  const [state, setState] = useState<'frage' | 'laeuft' | 'verworfen'>('frage');
  const { Payload, Display } = proposal;

  if (state === 'verworfen') {
    return null;
  }

  if (state === 'laeuft') {
    return (
      <KiKontrollstruktur
        start={startNurWurzel({
          art: 'gesellschaft',
          Bezeichnung: Payload.Firma,
          Firma: Payload.Firma,
          Registernummer: Payload.Registernummer,
          Registergericht: Payload.Registergericht,
        })}
        idLegalTransaction={Payload.idLegalTransaction}
        vorgangTitel={Display.Vorgang}
        idLegalClient={Payload.idLegalClient}
      />
    );
  }

  return (
    <Paper variant='outlined' sx={{ p: 1.5, borderColor: 'primary.light' }}>
      <Stack spacing={1}>
        <Stack direction='row' spacing={1} alignItems='center'>
          <AccountTree fontSize='small' color='primary' />
          <Typography variant='subtitle2'>
            Eigentümer- und Kontrollstruktur prüfen
          </Typography>
        </Stack>
        <Stack direction='row' spacing={0.5} flexWrap='wrap' useFlexGap>
          <Chip size='small' label={Display.Firma} />
          {Display.Register && (
            <Chip size='small' variant='outlined' label={Display.Register} />
          )}
          {Payload.idLegalTransaction !== null && (
            <Chip
              size='small'
              color='primary'
              variant='outlined'
              label={vorgangLabel(Display.Vorgang, Display.Aktenzeichen)}
            />
          )}
        </Stack>
        {proposal.Hinweise.length > 0 && (
          <Alert severity='info' sx={{ py: 0 }}>
            {proposal.Hinweise.join(' ')}
          </Alert>
        )}
        <Typography variant='caption' color='text.secondary'>
          Ich rufe die Gesellschafterliste bzw. den Registerauszug ab und
          verfolge beteiligte Gesellschaften weiter — je Gesellschaft ein
          Registerabruf, höchstens zehn.
        </Typography>
        <Stack direction='row' spacing={1} justifyContent='flex-end'>
          <Button
            size='small'
            color='inherit'
            startIcon={<Close />}
            onClick={() => setState('verworfen')}
          >
            Verwerfen
          </Button>
          <Button
            size='small'
            variant='contained'
            startIcon={<AccountTree />}
            onClick={() => setState('laeuft')}
          >
            Prüfung starten
          </Button>
        </Stack>
      </Stack>
    </Paper>
  );
};

/**
 * Vorschlagskarte: die zuletzt erstellte Struktur in einem Vorgang ablegen.
 *
 * Die Struktur lebt nur im Browser (`kontrollStore`). War die Prüfung bei
 * der letzten Frage noch nicht fertig, läuft sie hier weiter.
 */
export const KiKontrollstrukturAblageProposalCard: React.FC<{
  proposal: IKiKontrollstrukturAblageProposal;
}> = ({ proposal }) => {
  const [start] = useState<KontrollGraph | null>(() => kontrollStore.get());

  if (start === null) {
    return (
      <Alert severity='info'>
        In diesem Chat wurde noch keine Kontrollstruktur erstellt — oder die
        Seite wurde inzwischen neu geladen. Bitten Sie Karl, die Struktur der
        Gesellschaft zu prüfen; danach kann sie abgelegt werden.
      </Alert>
    );
  }

  return (
    <KiKontrollstruktur
      start={start}
      idLegalTransaction={proposal.Payload.idLegalTransaction}
      vorgangTitel={proposal.Display.Vorgang}
      speichernFragen
    />
  );
};
